
import { Booking } from "@/api/dtos/Booking.schema";
import api from "@/api";
import { BookingDetails } from "@/api/dtos/BookingDetails.schema";
import { rentalStatusToGerman } from "@/misc/i18n";
import { BookingsModule } from "@/store/modules/BookingsModule";
import moment from "moment";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import ContainerFrame from "../ContainerFrame.vue";
import DateRangePicker from "../DateRangePicker.vue";
import BookingDetailsDialog from "./BookingDetailsDialog.vue";
import CancelBookingDialog from "./CancelBookingDialog.vue";
import {
  formatBookingDateTime,
  hasBookingTimeZone,
} from "@/utils/bookingDateFormatter";
import { showToast } from "@/utils/toastService";
import { StationSummary } from "@/api/dtos/StationSummary.schema";
import { RentalStatus } from "@/api/dtos/RentalStatus.schema";
import { BookingDeviceFilterOption } from "@/api/dtos/BookingDeviceFilterOption";
import { StationsModule } from "@/store/modules/StationsModule";

@Component({
  components: {
    ContainerFrame,
    BookingDetailsDialog,
    DateRangePicker,
    CancelBookingDialog,
  },
})
export default class BookingsTable extends Vue {
  @Prop({
    default: 110,
  })
  width!: number;

  @Prop({
    default: 30,
  })
  height!: number;

  clickedBooking: BookingDetails | null = null;
  cancelBooking: Booking | null = null;
  showDetailsDialog = false;
  showConfirmationDialog = false;
  searchQuery = "";
  selectedStationId: string | null = null;
  selectedDeviceId: string | null = null;
  selectedStatus: RentalStatus | null = null;
  bookingDevices: BookingDeviceFilterOption[] = [];
  loadingDevices = false;
  deviceRequestId = 0;
  dateRange: [Date, Date] = [
    moment().subtract(7, "day").toDate(),
    moment().toDate(),
  ];

  rentalStatusToGerman = rentalStatusToGerman;
  moment = moment;
  formatBookingDateTime = formatBookingDateTime;
  hasBookingTimeZone = hasBookingTimeZone;

  async mounted(): Promise<void> {
    await StationsModule.fetchStationSummaries();

    const openedQueryBooking = await this.openBookingFromQuery();

    if (!openedQueryBooking) {
      await this.fetchBookingsForDateRange();
    }
    const query = this.$route.query as { [k: string]: string };
    // TODO evaluate query params
    // if (!query) return
    // if (query.booking) {
    //   this.queryBooking = query.booking
    // }
    // if (query.dates) {
    //   this.queryDates = this.stringToArray(query.dates)
    // }
    // TODO consider station filter
    // if (query.stations) {
    //   this.queryStations = this.stringToArray(query.stations)
    // }
  }

  onDateRangeChanged(dateRange: [Date, Date]): void {
    this.dateRange = dateRange;
    this.fetchBookingsForDateRange();
  }

  async onStationChanged(stationId: string | null): Promise<void> {
    this.selectedDeviceId = null;
    this.bookingDevices = [];

    const requestId = ++this.deviceRequestId;
    this.loadingDevices = false;

    if (stationId) {
      this.loadingDevices = true;

      try {
        const devices = await api.StationsApi.getBookingFilterDevices(stationId);

        if (requestId === this.deviceRequestId) {
          this.bookingDevices = devices;
        }
      }
      catch (error) {
        if (requestId === this.deviceRequestId) {
          showToast({
            message: "Die Boxen der Station konnten nicht geladen werden.",
            type: "error",
          });
        }
      }
      finally {
        if (requestId === this.deviceRequestId) {
          this.loadingDevices = false;
        }
      }
    }

    await this.fetchBookingsForDateRange();
  }

  onBookingFilterChanged(): void {
    this.fetchBookingsForDateRange();
  }

  @Watch("$route.query.booking")
  async onBookingQueryChanged(): Promise<void> {
    await this.openBookingFromQuery();
  }

  readonly headers: {
    text: string;
    value: string;
    align?: "start" | "center" | "end";
  }[] = [
    {
      text: "Buchungs-ID",
      value: "bookingId",
    },
    {
      text: "Station",
      value: "stationName",
    },
    {
      text: "Nutzer",
      value: "user.email",
    },
    {
      text: "Box",
      value: "device",
    },
    {
      text: "von",
      value: "start",
    },
    {
      text: "bis",
      value: "end",
    },
    {
      text: "Status",
      value: "status",
    },
    {
      text: "",
      value: "actions",
      align: "center",
    },
  ];

  get bookings(): Booking[] {
    return BookingsModule.bookings;
  }

  get clickedBookingDetails(): BookingDetails | null {
    return BookingsModule.detailedBooking;
  }

  get stations(): StationSummary[] {
    return StationsModule.stationSummaries;
  }

  get deviceOptions(): { text: string; value: string }[] {
    return this.bookingDevices.map((device) => ({
      text: `Box ${device.number}`,
      value: device.id,
    }));
  }

  readonly statusOptions: { text: string; value: RentalStatus }[] = ([
    "CREATED",
    "PAYED",
    "CONFIRMED",
    "RUNNING",
    "OVERDUE",
    "DONE",
    "CANCELLED",
  ] as RentalStatus[]).map((status) => ({
    text: rentalStatusToGerman(status),
    value: status,
  }));

  onRowClicked(booking: Booking): void {
    console.log(booking);
    BookingsModule.fetchDetailedBooking(booking.bookingId);
    this.showDetailsDialog = true;
  }

  onCancelBooking(booking: Booking): void{
    console.log(booking);
    this.cancelBooking = booking;
    this.showConfirmationDialog = true;
  }
  
  cancelBookingCancellation(): void{
    this.showConfirmationDialog = false;
    this.cancelBooking = null;
  }
  
  async deleteBooking(booking: Booking): Promise<void>{
    if(booking.bookingType != "USER"){
      showToast({
          message: "Es können nur manuell erstellte Buchung storniert werden!",
          type: "error",
        })
    }
    await api.BookingsApi.cancelUserBooking(booking.bookingId);
    this.showConfirmationDialog = false;
    this.cancelBooking = null;
    this.fetchBookingsForDateRange();
  }



  private async openBookingFromQuery(): Promise<boolean> {
    const bookingId = this.$route.query.booking;

    if (typeof bookingId !== "string" || !bookingId) {
      return false;
    }

    await BookingsModule.fetchDetailedBooking(bookingId);

    if (BookingsModule.detailedBooking) {
      this.dateRange = [
        moment(BookingsModule.detailedBooking.start).startOf("day").toDate(),
        moment(BookingsModule.detailedBooking.end).endOf("day").toDate(),
      ];

      await this.fetchBookingsForDateRange();
    }

    this.searchQuery = bookingId;
    this.showDetailsDialog = true;
    return true;
  }

  private async fetchBookingsForDateRange(): Promise<void> {
    await BookingsModule.fetchBookings({
      start: this.dateRange[0],
      end: this.dateRange[1],
      stationIds: this.selectedStationId ? [this.selectedStationId] : undefined,
      deviceIds: this.selectedDeviceId ? [this.selectedDeviceId] : undefined,
      status: this.selectedStatus ?? undefined,
    });
  }
}
